import { useAuthorization } from 'framework/authorization';
import type { FormFieldsData } from 'framework/form';
import { CreateEditFormDrawer } from 'framework/ViewTypes/contentLayout';
import { POSITION_FILL_REQUEST_STATUS } from '../../constants';
import type { PositionFillRequestListItem } from '../../types';
import { getEditPositionFillRequestSections } from './editPositionFillRequestSections';
import getEditPositionFillRequestValidation from './editPositionFillRequestValidation';
import type { EditPositionFillRequestFormValues } from './types';
import useEditPositionFillRequestService from './useEditPositionFillRequestService';
import { RECRUITMENT_POOL_UPDATE } from 'domain/recruiting/recruitmentPool/constants';
import { getExpirationPeriodInMonth } from './utils';

type EditPositionFillRequestDrawerProps = {
  isOpen: boolean;
  positionFillRequest: PositionFillRequestListItem;
  onClose: () => void;
  onSuccess: () => void;
};

const EditPositionFillRequestDrawer = ({
  isOpen,
  positionFillRequest,
  onClose,
  onSuccess,
}: EditPositionFillRequestDrawerProps) => {
  const { isGranted } = useAuthorization();
  const { savePositionFillRequest } = useEditPositionFillRequestService();
  const isActive = positionFillRequest.status === POSITION_FILL_REQUEST_STATUS.ACTIVE;
  const canEditRecruitmentPool = isGranted(RECRUITMENT_POOL_UPDATE);
  const initialData: EditPositionFillRequestFormValues = {
    ownerAlias: positionFillRequest.ownerAlias,
    positionSlotId: positionFillRequest.positionSlotId,
    recruitmentPoolId: positionFillRequest.recruitmentPoolId,
    expirationPeriodInMonth: getExpirationPeriodInMonth(
      positionFillRequest.createdAt,
      positionFillRequest.expirationDate,
    ),
    responsibilities: positionFillRequest.responsibilities,
    requiredExperience: positionFillRequest.requiredExperience,
    englishLevel: positionFillRequest.englishLevel,
    requirements: positionFillRequest.requirements,
    projectName: positionFillRequest.projectName,
    projectDescription: positionFillRequest.projectDescription,
    notes: positionFillRequest.notes,
  };
  const submitForm = (data: FormFieldsData) =>
    savePositionFillRequest(positionFillRequest, data as EditPositionFillRequestFormValues);

  return (
    <CreateEditFormDrawer
      key={`pfr-${positionFillRequest.id}-${isOpen}`}
      isOpen={isOpen}
      onClose={onClose}
      title="Edit position fill request"
      width={820}
      initialData={initialData}
      submitFormFunc={submitForm}
      submitButtonTitle="Save"
      getValidationSchemaFunc={() => Promise.resolve(getEditPositionFillRequestValidation(isActive))}
      handleDirty={false}
      afterSubmit={{
        execute: () => {
          onSuccess();
          onClose();
        },
        message: 'Position fill request has been updated.',
      }}
      getSections={() =>
        getEditPositionFillRequestSections({
          isActive,
          canEditRecruitmentPool,
          positionSlot: positionFillRequest.positionSlot,
          positionDisplayName: positionFillRequest.requestDisplayName,
        })
      }
    />
  );
};

export default EditPositionFillRequestDrawer;
